/**
 * SkyWrench service - calls the AETHER backend proxy to the SkyWrench troubleshooting API.
 * Triggers automatically when a defect is created/updated, stores results on the event record.
 */
import { createLogger } from '../utils/logger';
import { authedFetch } from './apiClient';
import { SkyWrenchResult, UnscheduledEvent } from '../types';
import { formatAtaCode } from './ataCodeService';
import { fetchFleetData, getAircraftTypeByTail } from './fleetService';

const logger = createLogger('SkyWrenchService');

/** Job statuses SkyWrench reports as still in progress. */
export const IN_PROGRESS_STATUSES = new Set(['PENDING', 'RUNNING', 'IN_PROGRESS']);

/**
 * Input parameters for a SkyWrench query.
 */
export interface SkyWrenchQueryInput {
  aircraftType: string;
  ataCode: string;
  defectDescription: string;
  tailNumber: string;
  carrier: string;
  station: string;
}

/**
 * Shape of the async job handle returned by the initial POST and of each poll.
 * `result` is present only once `status` reaches COMPLETED.
 */
export interface SkyWrenchJob {
  jobId?: string;
  status?: string;
  statusUrl?: string;
  result?: SkyWrenchResult['response'];
  error?: string;
}

/** Short-lived browser access returned for a SkyWrench source PDF. */
export interface SkyWrenchReferenceAccess {
  filename: string;
  url: string;
}

/**
 * Resolves a structured SkyWrench source URI through AETHER's authenticated
 * backend. The returned URL is short-lived and intended for the PDF viewer.
 */
export async function resolveSkyWrenchReference(
  sourceUri: string,
): Promise<SkyWrenchReferenceAccess> {
  try {
    const path: string = `/skywrench/reference?uri=${encodeURIComponent(sourceUri)}`;
    const response: Response | null = await authedFetch(path);
    if (!response) {
      throw new Error('SkyWrench reference could not be authenticated');
    }
    if (!response.ok) {
      const errorBody: string = await response.text();
      logger.error('SkyWrench reference returned error', {
        status: response.status,
        body: errorBody,
      });
      throw new Error(
        `SkyWrench reference error: ${response.status}${extractErrorDetail(errorBody)}`,
      );
    }
    const parsed: unknown = await response.json();
    if (!isSkyWrenchReferenceAccess(parsed)) {
      throw new Error('SkyWrench reference response was invalid');
    }
    return parsed;
  } catch (error: unknown) {
    logger.error('SkyWrench reference resolution failed', {
      error: error instanceof Error ? error.message : 'Unknown error',
    });
    throw error;
  }
}

/** Validates the document resolver response before its URL reaches an iframe. */
function isSkyWrenchReferenceAccess(value: unknown): value is SkyWrenchReferenceAccess {
  if (!value || typeof value !== 'object') return false;
  if (!('filename' in value) || !('url' in value)) return false;
  return (
    typeof value.filename === 'string' &&
    typeof value.url === 'string' &&
    value.url.startsWith('https://')
  );
}

/**
 * Submits a defect query to SkyWrench (via the AETHER proxy) and resolves with
 * the job handle. SkyWrench processes queries asynchronously, so this returns
 * immediately with a jobId; the caller persists the pending state and polls
 * (or relies on the backend poller) for completion. Uses the shared
 * authenticated client so the caller's Cognito token is attached and refreshed.
 */
export async function submitSkyWrenchJob(input: SkyWrenchQueryInput): Promise<SkyWrenchJob> {
  try {
    const response = await authedFetch('/skywrench/query', {
      method: 'POST',
      includeContentType: true,
      body: JSON.stringify(input),
    });

    if (!response) {
      throw new Error('SkyWrench query could not be authenticated');
    }
    if (!response.ok) {
      const errorBody = await response.text();
      logger.error('SkyWrench API returned error', { status: response.status, body: errorBody });
      throw new Error(`SkyWrench API error: ${response.status}${extractErrorDetail(errorBody)}`);
    }
    return (await response.json()) as SkyWrenchJob;
  } catch (err: unknown) {
    logger.error('SkyWrench job submission failed', {
      error: err instanceof Error ? err.message : 'Unknown error',
    });
    throw err;
  }
}

/**
 * Performs a single poll of an async job's status. Returns the parsed job
 * ({ status, result, ... }). The caller decides whether to continue polling
 * based on the status, so this stays a thin, independently testable call.
 */
export async function pollSkyWrenchJobOnce(jobId: string): Promise<SkyWrenchJob> {
  try {
    const response = await authedFetch(`/skywrench/query/${encodeURIComponent(jobId)}`);
    if (!response) {
      throw new Error('SkyWrench status poll could not be authenticated');
    }
    if (!response.ok) {
      const errorBody = await response.text();
      logger.error('SkyWrench status returned error', { status: response.status, body: errorBody });
      throw new Error(`SkyWrench API error: ${response.status}${extractErrorDetail(errorBody)}`);
    }
    return (await response.json()) as SkyWrenchJob;
  } catch (err: unknown) {
    logger.error('SkyWrench status poll failed', {
      error: err instanceof Error ? err.message : 'Unknown error',
    });
    throw err;
  }
}

/**
 * Persists a single defect's SkyWrench result onto the event record via the
 * targeted update endpoint (no revision bump, no audit). Registers/deregisters
 * the background poller job server-side based on the result status. Best-effort:
 * logs and swallows errors so a persistence hiccup never breaks the UI flow.
 */
export async function persistSkyWrenchResult(
  eventId: string,
  result: SkyWrenchResult,
): Promise<void> {
  if (!eventId) return; // Create-mode records have no id yet; persisted on Save.
  try {
    const response = await authedFetch(`/events/${encodeURIComponent(eventId)}/skywrench`, {
      method: 'PUT',
      includeContentType: true,
      body: JSON.stringify({ defectId: result.defectId, result }),
    });
    if (!response || !response.ok) {
      logger.error('Failed to persist SkyWrench result', {
        eventId,
        defectId: result.defectId,
        status: response ? response.status : 'no-response',
      });
    }
  } catch (err: unknown) {
    logger.error('persistSkyWrenchResult failed', {
      error: err instanceof Error ? err.message : 'Unknown error',
      eventId,
    });
  }
}

/**
 * Pulls the human-readable detail from the proxy's structured error body
 * ({ error, message, requestId }) so it can be appended to the thrown message
 * and shown on the defect. Returns an empty string when the body is not JSON
 * or carries no message.
 */
function extractErrorDetail(body: string): string {
  try {
    const parsed: unknown = JSON.parse(body);
    if (parsed && typeof parsed === 'object' && 'message' in parsed) {
      const detail = (parsed as { message?: unknown }).message;
      if (typeof detail === 'string' && detail) return ` — ${detail}`;
    }
  } catch {
    // Non-JSON body (e.g. an HTML error page); fall through to no detail.
  }
  return '';
}

/**
 * Builds a SkyWrenchResult object for a defect after a successful query.
 */
export function buildSkyWrenchResult(
  defectId: number,
  input: SkyWrenchQueryInput,
  response: SkyWrenchResult['response'],
  jobId?: string,
): SkyWrenchResult {
  return {
    defectId,
    status: 'complete',
    jobId,
    queriedAt: new Date().toISOString(),
    aircraftType: input.aircraftType,
    ataCode: input.ataCode,
    defectDescription: input.defectDescription,
    tailNumber: input.tailNumber,
    carrier: input.carrier,
    station: input.station,
    response,
  };
}

/**
 * Builds an error SkyWrenchResult when the API call fails.
 */
export function buildSkyWrenchError(
  defectId: number,
  input: SkyWrenchQueryInput,
  error: string,
  jobId?: string,
): SkyWrenchResult {
  return {
    defectId,
    status: 'error',
    jobId,
    queriedAt: new Date().toISOString(),
    aircraftType: input.aircraftType,
    ataCode: input.ataCode,
    defectDescription: input.defectDescription,
    tailNumber: input.tailNumber,
    carrier: input.carrier,
    station: input.station,
    error,
  };
}

/**
 * Builds a pending SkyWrenchResult (used while the analysis is running). Carries
 * the jobId so polling can resume across reopens and the backend poller can
 * materialize the result.
 */
export function buildSkyWrenchPending(
  defectId: number,
  input: SkyWrenchQueryInput,
  jobId: string,
): SkyWrenchResult {
  return {
    defectId,
    status: 'pending',
    jobId,
    queriedAt: new Date().toISOString(),
    aircraftType: input.aircraftType,
    ataCode: input.ataCode,
    defectDescription: input.defectDescription,
    tailNumber: input.tailNumber,
    carrier: input.carrier,
    station: input.station,
  };
}

/**
 * Resolves the ATA code string to send for a defect from its selected
 * chapter/section, normalizing the em-dash placeholder to an empty string.
 */
function resolveSkyWrenchAtaCode(chapter: string | undefined, section: string | undefined): string {
  const formatted = formatAtaCode(chapter || '', section || '');
  return formatted === '—' ? '' : formatted;
}

/**
 * Decides whether a defect needs a (re)generated SkyWrench analysis at save
 * time: it needs one when it has text and either has no result yet, a previous
 * error, or a completed result whose analyzed text no longer matches the
 * current defect text. A pending analysis is left to finish.
 */
function defectNeedsSkyWrench(text: string, existing: SkyWrenchResult | undefined): boolean {
  if (!text.trim()) return false;
  if (!existing) return true;
  if (existing.status === 'pending') return false;
  if (existing.status === 'error') return true;
  return existing.defectDescription !== text;
}

/**
 * Resolves the aircraft type for a SkyWrench query, falling back to the fleet
 * list (by tail number) when the event does not carry it. Triage-created events
 * frequently arrive without a resolved `aircraftType`, and SkyWrench rejects a
 * query missing it — so without this fallback the background run silently fails
 * for those defects. Best-effort: returns '' if the fleet lookup fails.
 */
async function resolveSkyWrenchAircraftType(event: UnscheduledEvent): Promise<string> {
  if (event.aircraftType) return event.aircraftType;
  if (!event.tailNumber) return '';
  // fetchFleetData is best-effort (returns [] on failure); the .catch keeps a
  // fleet-service hiccup from throwing so the query still proceeds with ''.
  const fleet = await fetchFleetData().catch(() => []);
  return getAircraftTypeByTail(fleet, event.tailNumber);
}

/**
 * Generates SkyWrench analyses for an event's defects when the record is
 * created or updated. Submits a job per qualifying defect and persists the
 * pending result onto the event so the backend poller can materialize it and
 * the analysis appears in the modal the next time the record is viewed.
 *
 * Runs in the background (best-effort): each defect is handled independently
 * and failures are recorded as an error result rather than thrown. Intended to
 * be called after the event has been persisted, when its id is known.
 */
export async function generateSkyWrenchForEvent(event: UnscheduledEvent): Promise<void> {
  if (!event.eventId) return;
  const results = event.skyWrenchResults || {};
  const chapters = event.selectedChapters || {};
  const sections = event.selectedSections || {};
  const pendingDefects = (event.defects || []).filter((defect) =>
    defectNeedsSkyWrench((defect.text || '').trim(), results[defect.id]),
  );
  if (pendingDefects.length === 0) return;

  // Resolve the aircraft type once (with a fleet fallback) for every defect on
  // this event, since they share the same tail. A lookup failure is non-fatal —
  // fall back to whatever the event carries so a query is still attempted.
  const aircraftType = await resolveSkyWrenchAircraftType(event).catch(
    () => event.aircraftType || '',
  );

  for (const defect of pendingDefects) {
    const defectText = (defect.text || '').trim();
    const input: SkyWrenchQueryInput = {
      aircraftType,
      ataCode: resolveSkyWrenchAtaCode(chapters[defect.id], sections[defect.id]),
      defectDescription: defectText,
      tailNumber: event.tailNumber,
      carrier: event.carrier,
      station: event.station,
    };

    try {
      const job = await submitSkyWrenchJob(input);
      if (!job.jobId) continue;
      await persistSkyWrenchResult(
        event.eventId,
        buildSkyWrenchPending(defect.id, input, job.jobId),
      );
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      void persistSkyWrenchResult(event.eventId, buildSkyWrenchError(defect.id, input, message));
    }
  }
}

/** Carriers supported by SkyWrench's carrier-to-knowledge-base configuration. */
export type StandaloneSkyWrenchCarrier = 'ABR' | 'ABX' | 'ASA' | 'ATN' | 'CSB' | 'FQA' | 'SCX';

export const STANDALONE_SKYWRENCH_CARRIERS: readonly StandaloneSkyWrenchCarrier[] = [
  'ABR',
  'ABX',
  'ASA',
  'ATN',
  'CSB',
  'FQA',
  'SCX',
];

/** One progressively generated standalone analysis section. */
export interface StandaloneSkyWrenchSection {
  section: string;
  title: string;
  content: string;
  error?: boolean;
  sourceUris?: string[];
}

interface StandaloneSkyWrenchSectionInput {
  carrier: StandaloneSkyWrenchCarrier;
  defectDescription: string;
  section: string;
}

/** True when an unknown select value is a supported standalone carrier. */
export function isStandaloneSkyWrenchCarrier(value: string): value is StandaloneSkyWrenchCarrier {
  return STANDALONE_SKYWRENCH_CARRIERS.some(
    (carrier: StandaloneSkyWrenchCarrier): boolean => carrier === value,
  );
}

function isStandaloneSkyWrenchSection(value: unknown): value is StandaloneSkyWrenchSection {
  if (!value || typeof value !== 'object') return false;
  const section: unknown = Reflect.get(value, 'section');
  const title: unknown = Reflect.get(value, 'title');
  const content: unknown = Reflect.get(value, 'content');
  const sourceUris: unknown = Reflect.get(value, 'sourceUris');
  return (
    typeof section === 'string' &&
    typeof title === 'string' &&
    typeof content === 'string' &&
    (sourceUris === undefined ||
      (Array.isArray(sourceUris) &&
        sourceUris.every((uri: unknown): boolean => typeof uri === 'string')))
  );
}

/** Requests one validated section for progressive standalone rendering. */
export async function requestStandaloneSkyWrenchSection(
  input: StandaloneSkyWrenchSectionInput,
): Promise<StandaloneSkyWrenchSection> {
  try {
    const response: Response | null = await authedFetch('/skywrench/standalone', {
      method: 'POST',
      includeContentType: true,
      body: JSON.stringify(input),
    });
    if (!response) throw new Error('SkyWrench analysis could not be authenticated');
    if (!response.ok) {
      const errorBody: string = await response.text();
      logger.error('Standalone SkyWrench section returned error', { status: response.status });
      throw new Error(`SkyWrench API error: ${response.status}${extractErrorDetail(errorBody)}`);
    }
    const parsed: unknown = await response.json();
    if (!isStandaloneSkyWrenchSection(parsed)) {
      throw new Error('SkyWrench section response was invalid');
    }
    return parsed;
  } catch (error: unknown) {
    logger.error('Standalone SkyWrench section request failed', {
      error: error instanceof Error ? error.message : 'Unknown error',
      section: input.section,
    });
    throw error;
  }
}
